import { parseAnalyticsBatch, type ClientAnalyticsEvent } from '@shared/index'
import { api } from '../api/http'
import { useAuthStore } from '../stores/auth'

type Props = Record<string, string | number | boolean>

const queue: { event: ClientAnalyticsEvent; props: Props }[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let flushing = false
let listening = false

const FLUSH_MS = 1500
const BATCH_MAX = 20

function ensureFlushHooks(): void {
  if (listening || typeof window === 'undefined') return
  listening = true
  const flushNow = () => {
    void flush()
  }
  window.addEventListener('pagehide', flushNow)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNow()
  })
}

/**
 * Queue a whitelisted client event. Best-effort; never throws.
 */
export function track(event: ClientAnalyticsEvent, props?: Props): void {
  ensureFlushHooks()
  queue.push({ event, props: props ?? {} })
  if (queue.length >= BATCH_MAX) {
    void flush()
    return
  }
  if (!timer) timer = setTimeout(() => void flush(), FLUSH_MS)
}

/**
 * Send this event before the caller continues. A coin toss uses it so the
 * open row leaves the queue before WebGL can freeze the tab.
 */
export function trackNow(event: ClientAnalyticsEvent, props?: Props): void {
  ensureFlushHooks()
  const item = { event, props: props ?? {} }
  if (flushing) {
    void postBatch([item])
    return
  }
  queue.push(item)
  void flush()
}

async function postBatch(
  batch: { event: ClientAnalyticsEvent; props: Props }[],
): Promise<void> {
  const auth = useAuthStore()
  if (!auth.token || batch.length === 0) return
  try {
    const events = parseAnalyticsBatch({ events: batch })
    if (events.length === 0) return
    await api('/analytics/batch', {
      method: 'POST',
      token: auth.token,
      quiet: true,
      body: JSON.stringify({ events }),
    })
  } catch {
    /* drop — analytics must never block play */
  }
}

async function flush(): Promise<void> {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (flushing || queue.length === 0) return
  const auth = useAuthStore()
  if (!auth.token) {
    queue.length = 0
    return
  }
  flushing = true
  const batch = queue.splice(0, BATCH_MAX)
  try {
    await postBatch(batch)
  } finally {
    flushing = false
    if (queue.length > 0) void flush()
  }
}
